import axios from "axios";

// In production (Docker/Plesk) this is left unset so requests go to a relative
// "/api" path, which nginx reverse-proxies to the backend container. For local
// dev, set REACT_APP_BACKEND_URL (e.g. http://localhost:8030) in frontend/.env.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

const TOKEN_KEY = "attomfit_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      setToken(null);
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  }
);

export async function login(username, password) {
  const { data } = await axios.post(`${API}/auth/login`, { username, password });
  setToken(data.token);
  return data;
}

export function logout() {
  setToken(null);
}

export const TIERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export const CATEGORIES = [
  { value: "car", label: "Car / Motor Tires" },
  { value: "motorbike", label: "Motorbike Tires" },
];

// Fixed "Categories" output value written into computed rows for each category.
export const CATEGORY_OUTPUT_LABELS = {
  car: "Car Tires",
  motorbike: "Motorbike Tires",
};

export async function fetchCsvInfo(category = "car", minQty) {
  const params = { category };
  if (minQty !== undefined) params.min_qty = minQty;
  const { data } = await api.get("/csv/info", { params });
  return data;
}
export async function fetchCsvPreview(limit = 20, category = "car") {
  const { data } = await api.get(`/csv/preview`, { params: { limit, category } });
  return data;
}
export async function fetchAllBrands() {
  const { data } = await api.get("/brands/all");
  return data;
}
export async function fetchMatrix(category = "car") {
  const { data } = await api.get("/matrix", { params: { category } });
  return data.rules;
}
export async function saveMatrix(rules, category = "car") {
  const { data } = await api.post("/matrix", { rules }, { params: { category } });
  return data.rules;
}
export async function resetMatrix(category = "car") {
  const { data } = await api.post("/matrix/reset", null, { params: { category } });
  return data.rules;
}
export async function fetchBrandTiers(category = "car") {
  const { data } = await api.get("/brand-tiers", { params: { category } });
  return data.tiers;
}
export async function saveBrandTiers(tiers, category = "car") {
  const { data } = await api.post("/brand-tiers", { tiers }, { params: { category } });
  return data.tiers;
}
export async function resetBrandTiers(category = "car") {
  const { data } = await api.post("/brand-tiers/reset", null, { params: { category } });
  return data.tiers;
}
export async function fetchMapping(category = "car") {
  const { data } = await api.get("/mapping", { params: { category } });
  return data.mapping;
}
export async function saveMapping(mapping, category = "car") {
  const { data } = await api.post("/mapping", mapping, { params: { category } });
  return data.mapping;
}
export async function fetchFtpConfig() {
  const { data } = await api.get("/ftp-config");
  return data;
}
export async function saveFtpConfig(config) {
  const { data } = await api.post("/ftp-config", config);
  return data;
}
export async function loadFromFtp(category = "car") {
  const { data } = await api.post("/ftp/load", null, { params: { category } });
  return data;
}
export async function runCompute(payload) {
  const { data } = await api.post("/compute", payload);
  return data;
}
function _downloadBlobResponse(response) {
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "download.csv";
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadCompute(category = "car") {
  const response = await api.get("/compute/download", { params: { category }, responseType: "blob" });
  _downloadBlobResponse(response);
}
export async function runCombinedCompute(payload) {
  const { data } = await api.post("/compute/combined", payload);
  return data;
}
export async function downloadCombinedCompute() {
  const response = await api.get("/compute/combined/download", { responseType: "blob" });
  _downloadBlobResponse(response);
}
